// src/routes/RDM/EcrCountriesPage.tsx
import React, { useMemo, useState } from "react";
import HeaderBanner from "../../components/Header";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, MapPin } from "lucide-react";

type Country = {
  name: string;
  flag: string;
  description: string;
  link: string;
};

const COUNTRIES: Country[] = [
  {
    name: "Ghana",
    flag: "/assets/images/flags/ghana-flag.jpg",
    description:
      "A significant outcome of the LIBSENSE RDM Workshop in Accra was the birth of the ECR Ghana Team and their ensuing activities.",
    link: "/ecr-ghana",
  },
  {
    name: "Nigeria",
    flag: "/assets/images/flags/nigeria.jpg",
    description:
      "The Nigeria team was borne out of the maiden RDM Workshop in Lagos.",
    link: "/ecr-nigeria",
  },
  // later: add more countries here
];

const ITEMS_PER_PAGE = 4;

const EcrCountriesPage: React.FC = () => {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    return COUNTRIES.filter((c) =>
      c.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [query]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  return (
    <>
      <HeaderBanner />

      <div className="max-w-5xl mx-auto pt-28 px-4 sm:px-6 lg:px-8 mb-12">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground text-center">
          Explore ECR Activities in African Countries
        </h1>

        {/* Search */}
        <div className="mt-6 flex justify-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search countries..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1); // reset to first page when searching
              }}
              className="pl-9 pr-3 py-2 w-full border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
          {paginated.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.35 }}
              className="bg-card rounded-xl shadow-sm ring-1 ring-border/50 overflow-hidden hover:shadow-md transition"
            >
              <img
                src={c.flag}
                alt={c.name}
                className="h-40 w-full object-cover"
              />
              <div className="p-4 flex flex-col justify-between">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" /> {c.name}
                </h3>
                <p className="text-sm text-foreground/80 mt-2 mb-4 line-clamp-3">
                  {c.description}
                </p>
                <Link
                  to={c.link}
                  className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`px-3 py-1 text-sm rounded ${
                  page === p
                    ? "bg-primary text-white"
                    : "bg-gray-100 dark:bg-night-600"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default EcrCountriesPage;
